import { Tabs as MantineTabs, TabsProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type TabsVariant = 'default' | 'outline' | 'pills' | 'gradient';

export interface ThemedTabsProps extends Omit<TabsProps, 'variant'> {
  tabsVariant?: TabsVariant;
  gradientVariant?: GradientVariant;
}

interface TabsComponent extends React.ForwardRefExoticComponent<ThemedTabsProps & React.RefAttributes<HTMLDivElement>> {
  List: typeof MantineTabs.List;
  Tab: typeof MantineTabs.Tab;
  Panel: typeof MantineTabs.Panel;
}

const Tabs = forwardRef<HTMLDivElement, ThemedTabsProps>(
  ({ tabsVariant = 'default', gradientVariant = 'primary', styles, children, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];

    const variantMap: Record<TabsVariant, TabsProps['variant']> = {
      default: 'default',
      outline: 'outline',
      pills: 'pills',
      gradient: 'pills',
    };

    if (tabsVariant !== 'gradient') {
      return (
        <MantineTabs ref={ref} variant={variantMap[tabsVariant]} styles={styles} {...rest}>
          {children}
        </MantineTabs>
      );
    }

    return (
      <MantineTabs
        ref={ref}
        variant="pills"
        styles={{
          ...styles,
          tab: {
            '&[data-active]': {
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              color: 'white',
            },
            ...(styles as any)?.tab,
          },
        }}
        {...rest}
      >
        {children}
      </MantineTabs>
    );
  }
) as TabsComponent;

Tabs.displayName = 'Tabs';

// Attach subcomponents
Tabs.List = MantineTabs.List;
Tabs.Tab = MantineTabs.Tab;
Tabs.Panel = MantineTabs.Panel;

export default Tabs;
